import { Text } from "pixi.js";
import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { Spine } from "pixi-spine";
import { Emitter } from "pixi-particles";
import { addToSpineSlot, resetSpine } from "utils/Misc";
import { BoundedContainer, MaxSize } from "utils/BoundedContainer";

type MeterSpines = {
    major: Spine,
    minor: Spine,
    mini: Spine
}

type MeterValues = {
    major: Text,
    minor: Text,
    mini: Text
}

type MeterBounds = {
    major: BoundedContainer,
    minor: BoundedContainer,
    mini: BoundedContainer
}

type MeterEmitters = {
    minor: [Emitter, Emitter],
    major: [Emitter, Emitter],
    mini: [Emitter, Emitter]
}

type JackpotSpec = {
    majorJackpot: any;
    minorJackpot: any;
    miniJackpot: any;
}

export class JackpotMeters extends View {

    private readonly meterSpines: MeterSpines;
    private readonly meterValues: MeterValues;
    private readonly meterEmitters: MeterEmitters;
    private readonly meterBounds: MeterBounds;

    constructor(protected override readonly services: GameServices, jackpotSpec: JackpotSpec) {
        super(services);

        const {factory, resources} = this.services;

        const valuesSpec = resources.getSpec(`jackpotmetervalues`);
        const emitterSpecs = resources.getSpec(`particles`);

        this.meterSpines = {
            major: factory.createSpine(jackpotSpec.majorJackpot, this),
            minor: factory.createSpine(jackpotSpec.minorJackpot, this),
            mini: factory.createSpine(jackpotSpec.miniJackpot, this),
        };

        this.meterValues = {
            major: factory.createText(``, valuesSpec.majorJackpotValue),
            minor: factory.createText(``, valuesSpec.minorJackpotValue),
            mini: factory.createText(``, valuesSpec.miniJackpotValue),
        };

        this.meterEmitters = {
            major: [
                factory.createParticleEmitter(emitterSpecs.jackpotMeterTrail),
                factory.createParticleEmitter(emitterSpecs.jackpotMeterTrail)
            ],
            minor: [
                factory.createParticleEmitter(emitterSpecs.jackpotMeterTrail),
                factory.createParticleEmitter(emitterSpecs.jackpotMeterTrail)
            ],
            mini: [
                factory.createParticleEmitter(emitterSpecs.jackpotMeterTrail),
                factory.createParticleEmitter(emitterSpecs.jackpotMeterTrail)
            ]
        };

        this.meterBounds = {
            major: new BoundedContainer(valuesSpec.majorBounds),
            minor: new BoundedContainer(valuesSpec.minorBounds),
            mini: new BoundedContainer(valuesSpec.miniBounds),
        };

        this.meterBounds.major.addChild(this.meterValues.major);
        this.meterBounds.minor.addChild(this.meterValues.minor);
        this.meterBounds.mini.addChild(this.meterValues.mini);

        addToSpineSlot(this.meterSpines.major, this.meterBounds.major, `dynamic-text`);
        addToSpineSlot(this.meterSpines.minor, this.meterBounds.minor, `dynamic-text`);
        addToSpineSlot(this.meterSpines.mini, this.meterBounds.mini, `dynamic-text`);

        addToSpineSlot(this.meterSpines.major, this.meterEmitters.major[0].parent, `particle-layer`);
        addToSpineSlot(this.meterSpines.major, this.meterEmitters.major[1].parent, `particle-layer`);
        addToSpineSlot(this.meterSpines.minor, this.meterEmitters.minor[0].parent, `particle-layer`);
        addToSpineSlot(this.meterSpines.minor, this.meterEmitters.minor[1].parent, `particle-layer`);
        addToSpineSlot(this.meterSpines.mini, this.meterEmitters.mini[0].parent, `particle-layer`);
        addToSpineSlot(this.meterSpines.mini, this.meterEmitters.mini[1].parent, `particle-layer`);

        this.reset();
    }

    tick() {
        this.setEmittersSpawnPositionToAnimatedLocation(this.meterEmitters.major, this.meterSpines.major);
        this.setEmittersSpawnPositionToAnimatedLocation(this.meterEmitters.minor, this.meterSpines.minor);
        this.setEmittersSpawnPositionToAnimatedLocation(this.meterEmitters.mini, this.meterSpines.mini);
    }

    private setEmittersSpawnPositionToAnimatedLocation(emitters: [Emitter, Emitter], meter: Spine) {
        this.setEmitterSpawnPositionToAnimatedLocation(emitters[0], meter, `dynamic-slot-1`);
        this.setEmitterSpawnPositionToAnimatedLocation(emitters[1], meter, `dynamic-slot-2`);
    }

    private setEmitterSpawnPositionToAnimatedLocation(emitter: Emitter, meter: Spine, slotName: string) {
        const movingSlot = meter.slotContainers[ meter.skeleton.findSlotIndex(slotName) ].children[0];
        const emitterContainer = meter.slotContainers[ meter.skeleton.findSlotIndex(`particle-layer`)  ];
        emitterContainer.toLocal(movingSlot.position, movingSlot, emitter.spawnPos, false);
    }

    setJackpotvalues(major: number, minor: number, mini: number) {
        const {currency} = this.services;

        this.meterValues.major.text = currency.formatCoins(major, {group: true, addTrailingZeros: false});
        this.meterValues.minor.text = currency.formatCoins(minor, {group: true, addTrailingZeros: false});
        this.meterValues.mini.text = currency.formatCoins(mini, {group: true, addTrailingZeros: false});

        this.meterBounds.major.update();
        this.meterBounds.minor.update();
        this.meterBounds.mini.update();
    }

    override reset() {
        resetSpine(this.meterSpines.major);
        resetSpine(this.meterSpines.minor);
        resetSpine(this.meterSpines.mini);

        this.meterSpines.major.state.setAnimation(0, `idle`, true);
        this.meterSpines.minor.state.setAnimation(0, `idle`, true);
        this.meterSpines.mini.state.setAnimation(0, `idle`, true);
    }

}